import type { Queue, SkillRequirement, StageRule } from "@/lib/types";
import { unknown, type Term } from "@/lib/campaign";

/**
 * A campaign's lines as the console shows and edits them: the vocabularies and their words, the
 * bounds, and the arithmetic between the line form and `PUT /queues/{id}`.
 *
 * Split out of `campaign.ts` beside `campaign-line-form.tsx`, which is the only screen that edits
 * one: the bounds mirror `LineSettings` in `core-api/src/schemas.py` and the server decides all of
 * them. Pure, for `test/campaign.test.ts`.
 */

/** The purposes a campaign adds. The primary line comes with the campaign. */
export const LINE_PURPOSES = ["closer", "inbound", "custom"] as const;

const PURPOSE: Record<string, Term> = {
  primary: {
    label: "Primary", description: "Created with the campaign. Calls to its numbers wait here.",
  },
  closer: {
    label: "Closers",
    description: "Where a handoff to a closer waits. Staffed like any line: put the closers' team on it.",
  },
  inbound: { label: "Inbound", description: "An inbound number with its own ring time and wait limit." },
  custom: { label: "Custom", description: "Anything else. A number routes to it by name." },
};

export function linePurposeText(purpose: string | null): Term {
  return purpose === null
    ? { label: "Standalone", description: "Not a campaign's line." }
    : PURPOSE[purpose] ?? unknown(purpose);
}

/** A campaign has one closer line and one inbound line at most; custom lines are unlimited. */
export function purposeTaken(purpose: string, lines: readonly Pick<Queue, "purpose">[]): boolean {
  return purpose !== "custom" && lines.some((line) => line.purpose === purpose);
}

/** How many reps one offer rings (ADR-049). `skill_strategy` orders them; this says how many. */
export const RING_STRATEGIES = ["one_at_a_time", "ring_all"] as const;

const RING: Record<string, Term> = {
  one_at_a_time: {
    label: "One rep at a time",
    description:
      "One rep rings alone, in the order Who rings first sets. If they do not answer in the ring time, the next one rings.",
  },
  ring_all: {
    label: "All reps at once",
    description: "Every free rep who may take the call rings together, and the first to answer gets it.",
  },
};

export function ringStrategyText(value: string): Term {
  return RING[value] ?? unknown(value);
}

/** The order a one-at-a-time line rings the reps who may take a call (ADR-015): `STRATEGIES` in
 *  the ACD's `matching.py`. A ring-all line rings them together, so the order is moot there. */
export const SKILL_STRATEGIES = ["longest_idle", "best_first", "conserve"] as const;

const SKILL_ORDER: Record<string, Term> = {
  longest_idle: {
    label: "Free longest first",
    description: "The rep who has been free the longest rings first.",
  },
  best_first: {
    label: "Highest level first",
    description:
      "The rep with the highest level in the skills the call needs rings first. A tie goes to the rep free the longest.",
  },
  conserve: {
    label: "Lowest level first",
    description:
      "The rep with the lowest level in the skills the call needs rings first, keeping the strongest reps free. A tie goes to the rep free the longest.",
  },
};

export function skillStrategyText(value: string): Term {
  return SKILL_ORDER[value] ?? unknown(value);
}

export const LINE_BOUNDS = {
  ring_timeout_seconds: [5, 120],
  wrap_up_seconds: [0, 900],
  disposition_seconds: [0, 900],
  max_wait_seconds: [0, 7200],
  default_priority: [0, 1000],
  skill_relax_seconds: [0, 3600],
} as const;

/** A line's settings form. Strings are `""` for a setting left to its default. */
export interface LineValues {
  description: string;
  ring_timeout_seconds: number;
  ring_strategy: string;
  skill_strategy: string;
  /** A rep must hold each of these to be offered the line's calls (ADR-015). In slug order.
   *  `preferred_skills` is the server's and is deliberately absent: the console never sends one
   *  (ACL-240), because the ACD counts a rep as preferred only while they hold every preferred
   *  skill at once, so a line's preference can cancel the campaign's Spanish one. */
  required_skills: SkillRequirement[];
  /** Seconds a caller waits for a rep with the skills the call needs — the line's, the number's,
   *  Spanish for a Spanish lead on `hard` language routing, `closer` for a handoff — before any rep
   *  on the line may answer. 0 never lifts them, with two exceptions the ACD owns: a handoff lifts
   *  on its own wait instead (`matching.py`'s handoff timer, the line's maximum or 120 s), and a
   *  caller a stage rule decided waits that rule's own fallback in place of this. */
  skill_relax_seconds: number;
  previous_rep_first: boolean;
  wrap_up_seconds: number;
  disposition_seconds: number;
  max_wait_seconds: number;
  default_priority: number;
  entry_announcement: string;
  moh: string;
  enabled: boolean;
  /** Who takes an inbound caller we know, by their contact's stage (ADR-073). */
  stage_rules: StageRule[];
}

/** `LINE_DEFAULTS` in core-api's `lines.py`: what a new line starts with. */
export const NEW_LINE: LineValues = {
  description: "",
  ring_timeout_seconds: 20,
  ring_strategy: "one_at_a_time",
  skill_strategy: "longest_idle",
  required_skills: [],
  skill_relax_seconds: 0,
  previous_rep_first: false,
  wrap_up_seconds: 10,
  disposition_seconds: 0,
  max_wait_seconds: 0,
  default_priority: 0,
  entry_announcement: "",
  moh: "",
  enabled: true,
  stage_rules: [],
};

export function lineValues(line: Queue): LineValues {
  return {
    description: line.description ?? "",
    ring_timeout_seconds: line.ring_timeout_seconds,
    ring_strategy: line.ring_strategy,
    skill_strategy: line.skill_strategy,
    required_skills: bySlug(line.required_skills ?? []),
    skill_relax_seconds: line.skill_relax_seconds ?? 0,
    previous_rep_first: line.previous_rep_first,
    wrap_up_seconds: line.wrap_up_seconds,
    disposition_seconds: line.disposition_seconds,
    max_wait_seconds: line.max_wait_seconds,
    default_priority: line.default_priority,
    entry_announcement: line.entry_announcement ?? "",
    moh: line.moh ?? "",
    enabled: line.enabled,
    stage_rules: (line.stage_rules ?? []).map((r) => ({ ...r, skills: r.skills.map((s) => ({ ...s })) })),
  };
}

/** The skill a closer line asks for by default. `DEFAULT_SKILLS` in platform seeds it in every
 *  tenant's catalog; an admin can take it off before saving. */
export const CLOSER_SKILL = "closer";

function purposeSkills(purpose: string): SkillRequirement[] {
  return purpose === "closer" ? [{ slug: CLOSER_SKILL, min_level: 1 }] : [];
}

/** What a new line of this purpose starts with: `NEW_LINE`, and a closer line needs `closer`, so
 *  a setter signed in to it is not offered a call a number routes there. */
export function newLineValues(purpose: string): LineValues {
  return { ...NEW_LINE, required_skills: purposeSkills(purpose) };
}

/**
 * The required skills after a new line's purpose changes from `from` to `to`: the new purpose's
 * default while the admin has left the old one's as it was, else what they picked, untouched.
 */
export function skillsForPurpose(
  from: string,
  to: string,
  current: readonly SkillRequirement[],
): SkillRequirement[] {
  const untouched = JSON.stringify(bySlug(current)) === JSON.stringify(purposeSkills(from));
  return untouched ? purposeSkills(to) : bySlug(current);
}

function bySlug(reqs: readonly SkillRequirement[]): SkillRequirement[] {
  return [...reqs]
    .map((r) => ({ slug: r.slug, min_level: r.min_level }))
    .sort((a, b) => (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0));
}

/** The settings changed against `line` — or, with no line, every one worth sending for a new line.
 *  An emptied text field is sent as null, which puts the column back to its default. A list (the
 *  stage rules) is compared by what it says and sent whole, as core-api replaces it whole. */
export function lineChanges(values: LineValues, line?: Queue): Record<string, unknown> {
  const before = line ? lineValues(line) : null;
  const out: Record<string, unknown> = {};
  const same = (a: unknown, b: unknown) =>
    Array.isArray(a) ? JSON.stringify(a) === JSON.stringify(b) : a === b;
  for (const key of Object.keys(values) as (keyof LineValues)[]) {
    const now = values[key];
    if (before ? same(before[key], now) : now === "" || (Array.isArray(now) && now.length === 0)) continue;
    out[key] = typeof now === "string" ? now.trim() || null : now;
  }
  return out;
}
